/**
 * Engagement Process Section - How to Get Started
 *
 * Clear, professional explanation of the engagement process
 * Background: Dark section with structured methodology
 */

import { SectionHeading, Muted } from "@/components/ui/section-heading";

const EngagementProcess = () => {
  return (
    <section
      id="pricing"
      className="relative bg-white/90 py-16 sm:py-24 lg:py-32 overflow-hidden shadow-2xl rounded-3xl"
    >
      {/* CONTENT CONTAINER */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
        {/* SECTION HEADER */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <SectionHeading
            variant="question"
            className="mb-4 sm:mb-6 text-center"
          >
            Getting Started
          </SectionHeading>
          <SectionHeading
            variant="headline"
            className="mb-8 sm:mb-12 lg:mb-16 text-center"
          >
            A systematic, data-driven approach
            <br />
            <Muted>from discovery to deployment.</Muted>
          </SectionHeading>
        </div>

        {/* TIMELINE STEPS */}
        <div className="relative">
          {/* VERTICAL LINE (hidden on mobile) */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#B87333] via-[#B87333]/50 to-transparent" />

          <div className="space-y-8 sm:space-y-12 lg:space-y-16">
            {/* STEP 1: INITIAL ASSESSMENT (FREE) */}
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              {/* LEFT SIDE - Content */}
              <div className="md:text-right">
                <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-xl inline-block max-w-[500px] w-full border-2 border-green-500">
                  <div className="flex items-center gap-3 mb-4 md:justify-end">
                    <div className="w-12 h-12 rounded-full bg-green-600 text-white font-black text-xl flex items-center justify-center flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900">
                        Initial Assessment
                      </h3>
                      <p className="text-xs font-bold text-green-600 uppercase tracking-wide">
                        Complimentary
                      </p>
                    </div>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Complete our questionnaire, then join us for a focused
                    1-hour Zoom consultation. We'll review your responses,
                    identify 2-3 immediate opportunities, and estimate potential
                    savings. No obligation—just insights.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Duration:</strong> 1 hour Zoom call
                      </p>
                    </div>
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Prerequisites:</strong> Completed questionnaire
                      </p>
                    </div>
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Investment:</strong>{" "}
                        <span className="text-green-600 font-bold">
                          Complimentary
                        </span>
                      </p>
                    </div>
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Deliverable:</strong> Opportunity summary,
                        estimated ROI
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CENTER DOT */}
              <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#B87333] border-4 border-slate-900" />

              {/* RIGHT SIDE - Empty for alternating layout */}
              <div className="hidden md:block" />
            </div>

            {/* STEP 2: DEEP DISCOVERY */}
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              {/* LEFT SIDE - Empty for alternating layout */}
              <div className="hidden md:block" />

              {/* CENTER DOT */}
              <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#B87333] border-4 border-slate-900" />

              {/* RIGHT SIDE - Content */}
              <div>
                <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-xl inline-block max-w-[500px] w-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#B87333] text-white font-black text-xl flex items-center justify-center flex-shrink-0">
                      2
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      Deep Discovery
                    </h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Comprehensive operational audit—mapping your current
                    processes, identifying inefficiencies, quantifying waste,
                    and documenting baseline metrics for comparison.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-slate-700">
                        <strong>Duration:</strong> 1-2 weeks
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-slate-700">
                        <strong>Investment:</strong>{" "}
                        <span className="text-[#B87333] font-bold">
                          $8,000 - $15,000
                        </span>
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-slate-700">
                        <strong>Deliverable:</strong> Process map, cost
                        analysis, opportunity report
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <p className="text-xs text-green-600 font-semibold">
                        💰 100% credited toward implementation
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 3: SOLUTION DESIGN */}
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              {/* LEFT SIDE - Content */}
              <div className="md:text-right">
                <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-xl inline-block max-w-[500px] w-full">
                  <div className="flex items-center gap-3 mb-4 md:justify-end">
                    <div className="w-12 h-12 rounded-full bg-[#B87333] text-white font-black text-xl flex items-center justify-center flex-shrink-0">
                      3
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      Solution Design
                    </h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Our team designs your optimized processes and automation
                    architecture—specifying exactly what will be automated, how
                    systems will integrate, and projected outcomes.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Duration:</strong> 1-3 weeks
                      </p>
                    </div>
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Investment:</strong>{" "}
                        <span className="text-[#B87333] font-bold">
                          $12,000 - $20,000
                        </span>
                      </p>
                    </div>
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Deliverable:</strong> Technical spec, ROI model,
                        implementation roadmap
                      </p>
                    </div>
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-xs text-green-600 font-semibold md:text-right">
                        💰 100% credited toward implementation
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CENTER DOT */}
              <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#B87333] border-4 border-slate-900" />

              {/* RIGHT SIDE - Empty for alternating layout */}
              <div className="hidden md:block" />
            </div>

            {/* STEP 4: IMPLEMENTATION */}
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              {/* LEFT SIDE - Empty for alternating layout */}
              <div className="hidden md:block" />

              {/* CENTER DOT */}
              <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#B87333] border-4 border-slate-900" />

              {/* RIGHT SIDE - Content */}
              <div>
                <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-xl inline-block max-w-[500px] w-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#B87333] text-white font-black text-xl flex items-center justify-center flex-shrink-0">
                      4
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      Implementation
                    </h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    We build, test, and deploy your new automated
                    processes—working in sprints with regular check-ins,
                    ensuring seamless integration with your existing systems.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-slate-700">
                        <strong>Duration:</strong> 4-12 weeks
                        (project-dependent)
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-slate-700">
                        <strong>Investment:</strong>{" "}
                        <span className="text-[#B87333] font-bold">
                          Custom quote
                        </span>
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <p className="text-sm text-slate-700">
                        <strong>Deliverable:</strong> Deployed solution,
                        documentation, training
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <p className="text-xs text-green-600 font-semibold">
                        💰 Discovery & design fees fully credited
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 5: OPTIMIZATION & SUPPORT */}
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              {/* LEFT SIDE - Content */}
              <div className="md:text-right">
                <div className="bg-white/90 rounded-2xl p-5 sm:p-8 shadow-xl inline-block max-w-[500px] w-full">
                  <div className="flex items-center gap-3 mb-4 md:justify-end">
                    <div className="w-12 h-12 rounded-full bg-[#B87333] text-white font-black text-xl flex items-center justify-center flex-shrink-0">
                      5
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      Ongoing Optimization
                    </h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Post-launch, we monitor performance against baseline
                    metrics, fine-tune automation rules, and provide ongoing
                    support to ensure sustained results and continuous
                    improvement.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Duration:</strong> Ongoing (monthly or quarterly
                        reviews)
                      </p>
                    </div>
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Investment:</strong>{" "}
                        <span className="text-[#B87333] font-bold">
                          Optional support package
                        </span>
                      </p>
                    </div>
                    <div className="flex items-start gap-2 md:justify-end">
                      <p className="text-sm text-slate-700 md:text-right">
                        <strong>Deliverable:</strong> Performance reports,
                        optimization recommendations
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CENTER DOT */}
              <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#B87333] border-4 border-slate-900" />

              {/* RIGHT SIDE - Empty for alternating layout */}
              <div className="hidden md:block" />
            </div>
          </div>
        </div>

        {/* WHAT YOU GET */}
        <div className="mt-10 sm:mt-16 lg:mt-20 bg-white/90 rounded-2xl p-5 sm:p-8 lg:p-12 shadow-xl border-2 border-slate-200">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 sm:mb-8 text-center">
            What You Get
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-slate-50/90 rounded-xl p-6 border-2 border-slate-200">
              <div className="w-10 h-10 rounded-lg bg-[#B87333] flex items-center justify-center mb-4">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">
                Complete Documentation
              </h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Process maps, technical specifications, user guides, and
                training materials for your team.
              </p>
            </div>

            <div className="bg-slate-50/90 rounded-xl p-6 border-2 border-slate-200">
              <div className="w-10 h-10 rounded-lg bg-[#B87333] flex items-center justify-center mb-4">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">
                Measurable Results
              </h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Before/after metrics, ROI tracking, and regular performance
                reports showing exactly what you're gaining.
              </p>
            </div>

            <div className="bg-slate-50/90 rounded-xl p-6 border-2 border-slate-200">
              <div className="w-10 h-10 rounded-lg bg-[#B87333] flex items-center justify-center mb-4">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-slate-900 mb-2">
                Ongoing Support
              </h4>
              <p className="text-slate-600 text-sm leading-relaxed">
                Dedicated support team, regular optimization reviews, and
                continuous improvement cycles.
              </p>
            </div>
          </div>
        </div>

        {/* INVESTMENT SUMMARY */}
        <div className="mt-10 sm:mt-16 lg:mt-20 bg-white/90 rounded-2xl p-5 sm:p-8 lg:p-12 shadow-xl border-2 border-slate-200">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 sm:mb-6 text-center">
            Transparent, Phased Investment
          </h3>
          <p className="text-lg text-slate-600 text-center max-w-[800px] mx-auto mb-8">
            Start with zero risk. Each phase builds on the previous one, and all
            discovery and design fees are credited toward implementation if you
            choose to proceed.
          </p>

          <div className="grid md:grid-cols-4 gap-4 max-w-[1000px] mx-auto mb-8">
            <div className="bg-green-50/90 rounded-xl p-4 border-2 border-green-500">
              <p className="text-xs text-green-700 font-bold uppercase mb-1">
                Phase 1
              </p>
              <p className="text-2xl font-black text-slate-900 mb-1">$0</p>
              <p className="text-xs text-slate-600">Initial Assessment</p>
            </div>
            <div className="bg-slate-50/90 rounded-xl p-4 border-2 border-slate-200">
              <p className="text-xs text-slate-600 font-bold uppercase mb-1">
                Phase 2
              </p>
              <p className="text-2xl font-black text-slate-900 mb-1">
                $8K-$15K
              </p>
              <p className="text-xs text-slate-600">Deep Discovery</p>
            </div>
            <div className="bg-slate-50/90 rounded-xl p-4 border-2 border-slate-200">
              <p className="text-xs text-slate-600 font-bold uppercase mb-1">
                Phase 3
              </p>
              <p className="text-2xl font-black text-slate-900 mb-1">
                $12K-$20K
              </p>
              <p className="text-xs text-slate-600">Solution Design</p>
            </div>
            <div className="bg-slate-50/90 rounded-xl p-4 border-2 border-slate-200">
              <p className="text-xs text-slate-600 font-bold uppercase mb-1">
                Phase 4
              </p>
              <p className="text-2xl font-black text-slate-900 mb-1">Custom</p>
              <p className="text-xs text-slate-600">Implementation</p>
            </div>
          </div>

          <p className="text-center text-sm text-slate-600 mb-8">
            💰 All discovery and design fees (Phases 2-3) are{" "}
            <strong className="text-green-700">100% credited</strong> toward
            implementation
          </p>

          <div className="text-center">
            <a
              href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-5 bg-[#B87333] text-white rounded-lg font-bold text-lg hover:bg-[#A66929] transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-[1.02]"
            >
              Schedule Your Free Assessment
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
            <p className="text-sm text-slate-600 mt-4">
              No cost, no obligation—just a clear understanding of your
              opportunities
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EngagementProcess;
